'use client';

import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';
import {isError} from '@epic-mw/error-types';
import {useAccountId} from '../account/account.hooks';
import {useAccountLoginRequired} from '../auth/auth.hooks';

import {
	EulaRes,
	getLatestEula,
	EulaAcceptedRes,
	createEulaKey,
	getEulaAccepted,
	EulaAcceptRes,
	acceptEula,
	getEulaVersion
} from './eula.fetch';

export const useEulaLatestContent = (
	key: string,
	locale: string
): {
	eulaContent?: EulaRes;
	isLoading: boolean;
	error?: string;
} => {
	const shouldFetch = !!key && !!locale;

	const {data, error} = useSWRImmutable(
		shouldFetch ? [key, locale] : null,
		([key, locale]) => getLatestEula(key, locale),
		{
			errorRetryCount: 1
		}
	);

	return {
		eulaContent: data,
		isLoading: !data && !error,
		error: isError(error) ? error.message : error
	};
};

/**
 * if provided locale doesn't exist, will return fallback 'en' locale
 * we should use that for agreement acceptance
 */
export const useEulaVersionContent = (
	key: string,
	version: number,
	locale: string
): {
	eulaContent?: EulaRes;
	isLoading: boolean;
	error?: string;
} => {
	const shouldFetch = !!key && !!locale && typeof version === 'number';

	const {data, error} = useSWRImmutable(
		shouldFetch ? [key, version, locale] : null,
		([key, version, locale]) => getEulaVersion(key, version, locale),
		{
			errorRetryCount: 1
		}
	);

	return {
		eulaContent: data,
		isLoading: !data && !error,
		error: isError(error) ? error.message : error
	};
};

export const useEulaAccepted = (
	key: string,
	locale: string
): {
	eulaAccepted?: EulaAcceptedRes;
	isLoading: boolean;
	error?: string;
} => {
	const {accountLoading} = useAccountLoginRequired(locale);
	const accountId = useAccountId();
	const shouldFetch = !accountLoading && key && accountId;
	const payload = {key, accountId, locale};

	const {data, error} = useSWR(
		shouldFetch ? createEulaKey(payload) : null,
		() => getEulaAccepted(payload),
		{
			errorRetryCount: 1
		}
	);

	return {
		eulaAccepted: data,
		isLoading: accountLoading || (!data && !error),
		error: isError(error) ? error.message : error
	};
};

export const useEulaAcceptance = (
	key: string,
	locale: string,
	submit: boolean,
	version?: number
): {
	accept?: EulaAcceptRes;
	isLoading: boolean;
	error?: string;
} => {
	const {accountLoading} = useAccountLoginRequired(locale);
	const accountId = useAccountId();
	const shouldFetch =
		!accountLoading && !!key && !!accountId && typeof version === 'number' && submit;

	const {data, error} = useSWRImmutable(
		shouldFetch ? createEulaKey({key, accountId, version, locale}) : null,
		() => acceptEula({key, accountId, version: Number(version), locale}),
		{
			errorRetryCount: 1
		}
	);

	return {
		accept: data,
		isLoading: accountLoading || (submit && !data && !error),
		error: isError(error) ? error.message : error
	};
};
